import { useState, useEffect, memo } from 'react';
import { motion } from 'motion/react';
import PixelIcon from '@/components/ui/PixelIcon';
import type { TimerProps } from '@/types/game';

const TimerComponent = ({ seconds: initialSeconds, minutes: initialMinutes, isRunning, isSlowed }: TimerProps) => {
  const [seconds, setSeconds] = useState(initialSeconds);
  const [minutes, setMinutes] = useState(initialMinutes);

  useEffect(() => {
    setSeconds(initialSeconds);
    setMinutes(initialMinutes);
  }, [initialSeconds, initialMinutes]);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isRunning) {
      interval = setInterval(() => {
        setSeconds(prevSeconds => {
          if (prevSeconds === 59) {
            setMinutes(prevMinutes => prevMinutes + 1);
            return 0;
          }
          return prevSeconds + 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isRunning]);

  const formatTime = (time: number) => {
    return time.toString().padStart(2, '0');
  };

  // Determine timer state based on time
  const getTimerClass = () => {
    const totalSeconds = minutes * 60 + seconds;

    if (totalSeconds >= 120) {
      return 'timer header timer-danger'; // 2+ minutes - danger state
    } else if (totalSeconds >= 60) {
      return 'timer header timer-warning'; // 1-2 minutes - warning state
    }
    return 'timer header'; // Normal state
  };

  const timeText = `${formatTime(minutes)}:${formatTime(seconds)}`;

  return (
    <div id="timer-header" className={getTimerClass()}>
      {isSlowed ? (
        <motion.span
          style={{ color: '#5dade2', display: 'inline-flex', alignItems: 'center', gap: 4 }}
          animate={{ scale: [1.0, 1.05, 1.0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <PixelIcon name="clock" size={16} color="#5dade2" />
          {timeText}
        </motion.span>
      ) : (
        timeText
      )}
    </div>
  );
};

// Memoize to prevent unnecessary re-renders
const Timer = memo(TimerComponent);

export default Timer;
